/**
 * Navigation resolver — the single place that decides what a user sees in the
 * rail and where they land. Every consumer (sidebar, settings checklist,
 * post-login redirect) routes through this so the rules live in exactly one
 * testable function instead of scattered conditionals.
 *
 * Resolution order (each step can only narrow what the previous one allowed,
 * except locks/always-visible which re-add *permitted* items):
 *
 *   1. candidates = catalog items the user's permissions allow
 *   2. remove admin `hiddenNavItems`            (rule: never visible)
 *   3. apply the work-profile preset            (hide items the profile omits,
 *      minus the rows a growing profile has grown into — see GROWN_NAV_ITEMS)
 *   4. apply user hide/pin                       (only if customisation allowed)
 *   5. force admin `lockedVisibleNavItems`       (only where permission exists)
 *   6. always-visible items (Settings) survive 3–4
 *   7. resolve the start page against the visible set, with safe fallback
 *
 * Invariants:
 *   - A user NEVER sees an item they lack permission for. Preferences and
 *     policy can only hide, never reveal.
 *   - Admin policy beats user preference at every contested step.
 *   - The start page is always a route the user can actually see.
 */
import { can, type UserRole } from "@/lib/users";
import {
  NAV_CATALOG,
  ALWAYS_VISIBLE_IDS,
  navItemById,
  type NavCatalogItem,
  type NavSectionId,
} from "./nav-catalog";
import {
  workProfilePreset,
  isWorkProfile,
  GROWN_NAV_ITEMS,
  GROWING_PROFILES,
  type WorkProfile,
} from "./work-profiles";
import { DEFAULT_NAV_PRESENCE, type NavPresence } from "./presence";
import { type NavigationPolicy } from "./policy";
import { type UserExperiencePreferences } from "./preferences";

export type NavHiddenReason = "permission" | "admin" | "profile" | "user";

export type ResolveNavigationInput = {
  /** Tenant role; null for an actor with no role (the resolver hides capped items). */
  role: UserRole | null;
  platformAdmin: boolean;
  policy: NavigationPolicy;
  preferences: UserExperiencePreferences;
  /**
   * Which work surfaces the tenant has produced. Only the growing profiles
   * (starter) consult it, and only to ADD a row. Omitted → assume everything
   * exists, i.e. hide nothing.
   */
  presence?: NavPresence;
};

export type ResolvedNavItem = NavCatalogItem & {
  /** Forced visible by an admin lock — surfaced so the UI can show a lock hint. */
  locked: boolean;
};

export type ResolvedNavigation = {
  /** Ordered visible items (catalog order), what the rail renders. */
  visible: ResolvedNavItem[];
  /** Permission-allowed candidates, regardless of profile/preference hiding. */
  allowed: NavCatalogItem[];
  /** Hidden candidates with the reason they were hidden (for the settings UI). */
  hidden: { item: NavCatalogItem; reason: NavHiddenReason }[];
  /** Effective work profile after clamping to the policy allow-list. */
  effectiveProfile: WorkProfile;
  /** Resolved landing nav-item id (always in `visible`). */
  startPageId: string;
  /** Resolved landing route (always reachable). */
  startPageHref: string;
};

/** A user is permitted an item when it has no cap, or `can(role, cap)`. */
function hasPermission(item: NavCatalogItem, role: UserRole | null): boolean {
  if (!item.cap) return true;
  if (!role) return false;
  return can(role, item.cap);
}

/**
 * Choose the effective profile: the user's pick when the policy allows it,
 * else the policy default, else the first allowed profile.
 */
function resolveEffectiveProfile(
  policy: NavigationPolicy,
  prefs: UserExperiencePreferences,
): WorkProfile {
  const allowed = policy.allowedWorkProfiles;
  const wanted = prefs.workProfile;
  if (isWorkProfile(wanted) && allowed.includes(wanted)) return wanted;
  if (allowed.includes(policy.defaultWorkProfile)) return policy.defaultWorkProfile;
  return allowed[0] ?? policy.defaultWorkProfile;
}

export function resolveNavigationForUser(input: ResolveNavigationInput): ResolvedNavigation {
  const { role, policy, preferences: prefs } = input;

  const effectiveProfile = resolveEffectiveProfile(policy, prefs);
  const preset = workProfilePreset(effectiveProfile);
  const presetShowsAll = preset.nav === "all";
  const presetSet = presetShowsAll ? null : new Set(preset.nav as readonly string[]);

  const presence = input.presence ?? DEFAULT_NAV_PRESENCE;
  const grows = GROWING_PROFILES.has(effectiveProfile);
  const hasGrownInto = (id: string): boolean => {
    if (!grows) return false;
    const signal = GROWN_NAV_ITEMS[id];
    return signal !== undefined && presence[signal] === true;
  };

  const hiddenByAdmin = new Set(policy.hiddenNavItems);
  const lockedByAdmin = new Set(policy.lockedVisibleNavItems);
  const customisationOn = policy.allowUserNavigationCustomization;
  const userHidden = customisationOn ? new Set(prefs.hiddenNavItems) : new Set<string>();
  const userPinned = customisationOn ? new Set(prefs.pinnedNavItems) : new Set<string>();

  const allowed: NavCatalogItem[] = [];
  const visible: ResolvedNavItem[] = [];
  const hidden: { item: NavCatalogItem; reason: NavHiddenReason }[] = [];

  for (const item of NAV_CATALOG) {
    // 1. Permission is the hard gate.
    if (!hasPermission(item, role)) {
      hidden.push({ item, reason: "permission" });
      continue;
    }
    allowed.push(item);

    // 2. Admin-hidden: never visible, beats everything below.
    if (hiddenByAdmin.has(item.id)) {
      hidden.push({ item, reason: "admin" });
      continue;
    }

    const alwaysVisible = ALWAYS_VISIBLE_IDS.has(item.id);
    const locked = lockedByAdmin.has(item.id);

    // 3. Profile preset baseline. A growing profile (starter) also shows a
    //    pipeline row once the tenant has actually produced one of those —
    //    the rail fills in as the workspace does, instead of presenting four
    //    empty pages on day one.
    let show = presetShowsAll || presetSet!.has(item.id) || hasGrownInto(item.id);
    let reason: NavHiddenReason = "profile";

    // 4. User hide/pin (only when customisation is allowed).
    if (userHidden.has(item.id)) {
      show = false;
      reason = "user";
    }
    if (userPinned.has(item.id)) {
      show = true;
    }

    // 5 + 6. Admin lock and always-visible re-add permitted items the profile
    // or the user would otherwise drop. Admin lock outranks a user hide.
    if (locked || alwaysVisible) show = true;

    if (show) {
      visible.push({ ...item, locked });
    } else {
      hidden.push({ item, reason });
    }
  }

  const { startPageId, startPageHref } = resolveStartPage(input, effectiveProfile, visible);

  return { visible, allowed, hidden, effectiveProfile, startPageId, startPageHref };
}

const FALLBACK_HREF = "/app";

/**
 * Resolve the landing page against the *visible* set so a user never lands on
 * a route they can't see. Order: user choice (if allowed) → profile default →
 * dashboard → first visible item.
 */
function resolveStartPage(
  input: ResolveNavigationInput,
  effectiveProfile: WorkProfile,
  visible: ResolvedNavItem[],
): { startPageId: string; startPageHref: string } {
  const visibleIds = new Set(visible.map((i) => i.id));
  const pick = (id: string | undefined): { startPageId: string; startPageHref: string } | null => {
    if (!id || !visibleIds.has(id)) return null;
    const item = navItemById(id);
    return item ? { startPageId: id, startPageHref: item.href } : null;
  };

  if (input.policy.allowUserStartPageCustomization) {
    const userPick = pick(input.preferences.startPage);
    if (userPick) return userPick;
  }
  const profilePick = pick(workProfilePreset(effectiveProfile).startPage);
  if (profilePick) return profilePick;

  const dashboard = pick("dashboard");
  if (dashboard) return dashboard;

  const first = visible[0];
  if (first) return { startPageId: first.id, startPageHref: first.href };
  return { startPageId: "dashboard", startPageHref: FALLBACK_HREF };
}

/** Group a resolved visible list by section, preserving catalog + section order. */
export function groupVisibleBySection(
  visible: ResolvedNavItem[],
): { section: NavSectionId; items: ResolvedNavItem[] }[] {
  const order: NavSectionId[] = ["overview", "work", "aiResults", "context", "aiOps", "platform"];
  return order
    .map((section) => ({ section, items: visible.filter((i) => i.section === section) }))
    .filter((g) => g.items.length > 0);
}
